import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Grid,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Table,
  Alert,
  type TableColumnProps,
} from '@arco-design/web-react';
import {
  IconArrowLeft,
  IconEdit,
  IconLock,
  IconMinusCircle,
  IconPlusCircle,
  IconRefresh,
  IconUserGroup,
} from '@arco-design/web-react/icon';
import dayjs from 'dayjs';
import { MoneyInput, PageHeader, SensitiveText, StatusPill, VirtualCard } from '@/components/ui';
import { billingApi, cardsApi, transactionsApi } from '@/api';
import type { FeePreviewResult } from '@/api/cards';
import type { BillingProfile } from '@/types/billing';
import type { CardSecret, VccCard } from '@/types/card';
import type {
  SettlementStatus,
  TransactionDirection,
  TransactionListQuery,
  TransactionRecord,
} from '@/types/transaction';
import { centsToDollars, dollarsToCents, formatMoney, formatPercent } from '@/utils/money';
import { useAuthStore } from '@/store/authStore';

const { Row, Col } = Grid;
const { RangePicker } = DatePicker;

const directionOptions: Array<{ label: string; value: TransactionDirection | 'ALL' }> = [
  { label: '全部流向', value: 'ALL' },
  { label: '消费', value: 'PURCHASE' },
  { label: '退款', value: 'REFUND' },
];

const statusOptions: Array<{ label: string; value: SettlementStatus | 'ALL' }> = [
  { label: '全部状态', value: 'ALL' },
  { label: '处理中', value: 'PROCESSING' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '被拒绝', value: 'REJECTED' },
  { label: '已完成', value: 'COMPLETED' },
];

const statusToneMap: Record<SettlementStatus, 'processing' | 'success' | 'warning' | 'danger'> = {
  PROCESSING: 'processing',
  CONFIRMED: 'success',
  REJECTED: 'danger',
  COMPLETED: 'success',
};

export default function CardDetailView() {
  const { cardId = '' } = useParams();
  const navigate = useNavigate();
  const refreshProfile = useAuthStore((s) => s.refreshProfile);

  const [card, setCard] = useState<VccCard | null>(null);
  const [billing, setBilling] = useState<BillingProfile | null>(null);
  const [secret, setSecret] = useState<CardSecret | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [refillForm] = Form.useForm<{ amount: number; remark?: string }>();
  const [withdrawForm] = Form.useForm<{ amount: number; remark?: string }>();
  const [limitForm] = Form.useForm<{ totalLimit: number }>();
  const [refillFeePreview, setRefillFeePreview] = useState<FeePreviewResult | null>(null);

  const [txnQuery, setTxnQuery] = useState<TransactionListQuery>({
    page: 1,
    pageSize: 20,
    cardId,
  });
  const [txnList, setTxnList] = useState<TransactionRecord[]>([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnLoading, setTxnLoading] = useState(false);

  const loadCard = useCallback(async () => {
    if (!cardId) return;
    const detail = await cardsApi.detail(cardId);
    setCard(detail);
    const profile = await billingApi.detail(detail.billingProfileId);
    setBilling(profile);
  }, [cardId]);

  const fetchTransactions = useCallback(async () => {
    if (!cardId) return;
    setTxnLoading(true);
    try {
      const result = await transactionsApi.list({ ...txnQuery, cardId });
      setTxnList(result.items);
      setTxnTotal(result.total);
    } finally {
      setTxnLoading(false);
    }
  }, [cardId, txnQuery]);

  useEffect(() => {
    setLoading(true);
    loadCard()
      .catch((e: unknown) => {
        if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
          Message.error(e.message);
        }
        navigate('/cards');
      })
      .finally(() => setLoading(false));
  }, [loadCard, navigate]);

  useEffect(() => {
    setTxnQuery((prev) => ({ ...prev, cardId, page: 1 }));
  }, [cardId]);

  useEffect(() => {
    fetchTransactions().catch(() => undefined);
  }, [fetchTransactions]);

  const updateTxnQuery = (next: Partial<TransactionListQuery>) =>
    setTxnQuery((prev) => ({ ...prev, ...next, page: 1 }));

  const handleReveal = async (): Promise<string> => {
    if (!card) return '';
    const result = await cardsApi.revealSecret(card.id);
    setSecret(result);
    return result.pan;
  };

  const reloadCard = async () => {
    await loadCard();
  };

  const handleRefreshBalance = async () => {
    if (!card) return;
    try {
      setBalanceRefreshing(true);
      const updated = await cardsApi.refreshBalance(card.id);
      setCard(updated);
      Message.success('可用余额已更新');
    } catch (e) {
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setBalanceRefreshing(false);
    }
  };

  const handleRefill = async () => {
    if (!card) return;
    try {
      const v = await refillForm.validate();
      setSubmitting(true);
      await cardsApi.refill(card.id, {
        amountCents: dollarsToCents(v.amount),
        remark: v.remark,
      });
      Message.success('充值已提交，等待上游清算');
      refillForm.resetFields();
      await reloadCard();
      await refreshProfile();
    } catch (e) {
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!card) return;
    try {
      const v = await withdrawForm.validate();
      setSubmitting(true);
      await cardsApi.withdraw(card.id, {
        amountCents: dollarsToCents(v.amount),
        remark: v.remark,
      });
      Message.success('提现已提交，等待上游清算');
      withdrawForm.resetFields();
      await reloadCard();
      await refreshProfile();
    } catch (e) {
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openLimitModal = () => {
    if (!card) return;
    limitForm.setFieldsValue({ totalLimit: centsToDollars(card.totalLimitCents) });
    setLimitModalOpen(true);
  };

  const handleUpdateLimit = async () => {
    if (!card) return;
    try {
      const v = await limitForm.validate();
      setSubmitting(true);
      const updated = await cardsApi.updateLimit(card.id, {
        totalLimitCents: dollarsToCents(v.totalLimit),
      });
      setCard(updated);
      Message.success('消费限额已更新');
      setLimitModalOpen(false);
      limitForm.resetFields();
    } catch (e) {
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (!card) return;
    try {
      setSubmitting(true);
      await cardsApi.close(card.id);
      Message.success('销卡指令已下发');
      await reloadCard();
      await refreshProfile();
    } catch (e) {
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const txnColumns: TableColumnProps<TransactionRecord>[] = [
    {
      title: '记账时间',
      dataIndex: 'bookedAt',
      width: 160,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: 'MCC', dataIndex: 'mcc', width: 80 },
    { title: '商户', dataIndex: 'merchantName', ellipsis: true },
    {
      title: '流向',
      dataIndex: 'direction',
      width: 80,
      render: (v: TransactionDirection) => (v === 'PURCHASE' ? '消费' : '退款'),
    },
    {
      title: '金额',
      dataIndex: 'amountCents',
      width: 120,
      align: 'right',
      render: (v: number, r) => (
        <span
          style={{
            color: r.direction === 'REFUND' ? 'var(--up-color-success)' : 'var(--up-color-text-primary)',
            fontFamily: 'SFMono-Regular, Consolas, monospace',
          }}
        >
          {r.direction === 'REFUND' ? '+' : '-'} {formatMoney(Math.abs(v))}
        </span>
      ),
    },
    {
      title: '清算手续费',
      dataIndex: 'feeCents',
      width: 120,
      align: 'right',
      render: (v: number) => formatMoney(v),
    },
    {
      title: '清算状态',
      dataIndex: 'status',
      width: 100,
      render: (v: SettlementStatus) => (
        <StatusPill tone={statusToneMap[v]}>
          {statusOptions.find((s) => s.value === v)?.label ?? v}
        </StatusPill>
      ),
    },
    {
      title: '网络交易号',
      dataIndex: 'networkTxnId',
      width: 200,
      ellipsis: true,
    },
    {
      title: '失败原因',
      dataIndex: 'failureReason',
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#86909C' }}>—</span>,
    },
  ];

  if (loading || !card) {
    return (
      <div className="up-page">
        <Skeleton animation text={{ rows: 10 }} />
      </div>
    );
  }

  const actionsDisabled = card.status !== 'ACTIVE';

  return (
    <div className="up-page">
      <PageHeader
        title={card.alias || '虚拟卡详情'}
        subtitle="卡信息、额度操作与消费明细"
        extra={
          <Button icon={<IconArrowLeft />} onClick={() => navigate('/cards')}>
            返回卡包
          </Button>
        }
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          <div className="up-section-card" style={{ padding: 16, height: '100%' }}>
            <VirtualCard
              alias={card.alias}
              brand={card.brand}
              cardholderName={
                billing ? `${billing.firstName} ${billing.lastName}`.trim() : '—'
              }
              maskedPan={card.maskedPan}
              expireMonth={card.expireMonth}
              expireYear={card.expireYear}
              status={card.status}
            />
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div className="up-section-card" style={{ padding: 16, height: '100%' }}>
            <div
              style={{
                fontWeight: 600,
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>卡片信息</span>
              <Button
                type="outline"
                size="small"
                icon={<IconEdit />}
                disabled={actionsDisabled}
                onClick={openLimitModal}
              >
                调额
              </Button>
            </div>
            <Descriptions
              column={1}
              colon=" : "
              data={[
                {
                  label: '卡号',
                  value: <SensitiveText masked={card.maskedPan} onReveal={handleReveal} />,
                },
                {
                  label: 'CVV',
                  value: secret ? (
                    <SensitiveText masked="***" plain={secret.cvv} />
                  ) : (
                    <span style={{ color: 'var(--up-color-text-tertiary)' }}>请先解密卡号</span>
                  ),
                },
                {
                  label: '有效期',
                  value: `${String(card.expireMonth).padStart(2, '0')} / ${card.expireYear}`,
                },
                {
                  label: '可用余额',
                  value: (
                    <Space size={4}>
                      <span>{formatMoney(card.availableBalanceCents)}</span>
                      <Button
                        size="mini"
                        type="text"
                        loading={balanceRefreshing}
                        icon={<IconRefresh />}
                        onClick={handleRefreshBalance}
                      />
                    </Space>
                  ),
                },
                { label: '单笔限额', value: formatMoney(card.totalLimitCents) },
                { label: '可重复充值', value: card.reloadable ? '是' : '否' },
                {
                  label: '创建时间',
                  value: dayjs(card.createdAt).format('YYYY-MM-DD HH:mm:ss'),
                },
              ]}
            />
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div className="up-section-card" style={{ padding: 16, height: '100%' }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              <IconUserGroup style={{ marginRight: 6 }} />
              持卡人信息
            </div>
            {billing ? (
              <Descriptions
                column={1}
                colon=" : "
                data={[
                  { label: '姓名', value: `${billing.firstName} ${billing.lastName}` },
                  { label: '电话', value: billing.phone },
                  { label: '国家', value: billing.country },
                  { label: '州/省', value: billing.state },
                  { label: '城市', value: billing.city },
                  { label: '街道', value: billing.street },
                  { label: '邮编', value: billing.postalCode },
                ]}
              />
            ) : (
              <Empty description="未找到持卡人模板" />
            )}
          </div>
        </Col>
      </Row>

      <div className="up-section-card" style={{ padding: 16, marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              <IconPlusCircle style={{ color: 'var(--up-color-success)' }} /> 充值
            </div>
            <Form
              form={refillForm}
              layout="vertical"
              disabled={actionsDisabled}
              onValuesChange={(changes) => {
                if ('amount' in changes && changes.amount) {
                  void cardsApi
                    .feePreview({ kind: 'REFILL', amountCents: dollarsToCents(changes.amount) })
                    .then(setRefillFeePreview)
                    .catch(() => setRefillFeePreview(null));
                }
              }}
            >
              <Form.Item label="充值金额（USD）" field="amount" rules={[{ required: true }]}>
                <MoneyInput minDollar={1} />
              </Form.Item>
              {refillFeePreview ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  content={
                    <div>
                      入账手续费 ({formatPercent(refillFeePreview.depositFeeRateBps)}):{' '}
                      {formatMoney(refillFeePreview.feeCents)} · 实际到卡:{' '}
                      {formatMoney(refillFeePreview.netCents)}
                    </div>
                  }
                />
              ) : null}
              <Form.Item label="备注" field="remark">
                <Input placeholder="可选" />
              </Form.Item>
              <Button
                type="primary"
                loading={submitting}
                disabled={actionsDisabled}
                onClick={handleRefill}
              >
                确认充值
              </Button>
            </Form>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              <IconMinusCircle style={{ color: 'var(--up-color-warning)' }} /> 余额转出
            </div>
            <Form form={withdrawForm} layout="vertical" disabled={actionsDisabled}>
              <Form.Item label="转出金额（USD）" field="amount" rules={[{ required: true }]}>
                <MoneyInput minDollar={0.01} />
              </Form.Item>
              <Form.Item label="备注" field="remark">
                <Input placeholder="可选" />
              </Form.Item>
              <Button
                status="warning"
                loading={submitting}
                disabled={actionsDisabled}
                onClick={handleWithdraw}
              >
                转回母账户
              </Button>
            </Form>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              <IconLock style={{ color: 'var(--up-color-danger)' }} /> 立即销卡
            </div>
            <div style={{ color: 'var(--up-color-danger)', marginBottom: 12, fontSize: 13 }}>
              销卡操作不可逆，剩余余额将自动退回母账户。
            </div>
            <Popconfirm
              focusLock
              title="确认销卡？"
              content="销卡后该卡片立即失效，剩余美分将清算回母账户"
              onOk={handleClose}
              disabled={actionsDisabled}
            >
              <Button status="danger" loading={submitting} disabled={actionsDisabled}>
                立即销卡
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </div>

      <div className="up-section-card" style={{ padding: 16 }}>
        <div
          style={{
            fontWeight: 600,
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>消费明细</span>
          <Button icon={<IconRefresh />} size="small" onClick={() => fetchTransactions()}>
            刷新
          </Button>
        </div>
        <Space wrap style={{ marginBottom: 12 }}>
          <RangePicker
            onChange={(dateStrings) =>
              updateTxnQuery({
                startDate: dateStrings?.[0] || undefined,
                endDate: dateStrings?.[1] || undefined,
              })
            }
          />
          <Select
            placeholder="流向"
            style={{ width: 140 }}
            value={txnQuery.direction ?? 'ALL'}
            onChange={(v) =>
              updateTxnQuery({ direction: v === 'ALL' ? undefined : (v as TransactionDirection) })
            }
          >
            {directionOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="清算状态"
            style={{ width: 160 }}
            value={txnQuery.status ?? 'ALL'}
            onChange={(v) =>
              updateTxnQuery({ status: v === 'ALL' ? undefined : (v as SettlementStatus) })
            }
          >
            {statusOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        </Space>
        <Table
          rowKey="id"
          loading={txnLoading}
          data={txnList}
          columns={txnColumns}
          scroll={{ x: 1100 }}
          pagination={{
            total: txnTotal,
            current: txnQuery.page,
            pageSize: txnQuery.pageSize,
            sizeCanChange: true,
            showTotal: true,
            onChange: (page, pageSize) => setTxnQuery((p) => ({ ...p, page, pageSize })),
          }}
        />
      </div>

      <Modal
        title="调整消费限额"
        visible={limitModalOpen}
        onCancel={() => {
          setLimitModalOpen(false);
          limitForm.resetFields();
        }}
        onOk={handleUpdateLimit}
        confirmLoading={submitting}
        okText="确认调额"
        cancelText="取消"
      >
        <p style={{ color: 'var(--up-color-text-secondary)', marginBottom: 16 }}>
          将调用上游 Update a card 接口，重新设置该卡的消费限额（spend limitation）。
          当前限额：{formatMoney(card.totalLimitCents)}
        </p>
        <Form form={limitForm} layout="vertical">
          <Form.Item
            label="新的消费限额（USD）"
            field="totalLimit"
            rules={[{ required: true, message: '请输入新的消费限额' }]}
          >
            <MoneyInput minDollar={1} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
